import { ObjectPool } from "../types/Objects";
import { create } from "zustand";
import Api from "../utils/Api";
import { getYoutubeCategories, YoutubeCategory } from "../utils/api/Youtube";

interface State {
  /**
   * Array of ObjectPool entities.
   */
  objects: ObjectPool[];

  /**
   * Indicates whether the ObjectPool entities have been loaded.
   */
  loaded: boolean;

  /**
   * Array of YouTube categories.
   */
  youtubeCategories: YoutubeCategory[];
}

interface Actions {
  /**
   * Returns all ObjectPool entities.
   * @return An array of ObjectPool entities
   */
  getObjects: () => ObjectPool[];

  /**
   * Returns whether the ObjectPool entities have been loaded.
   * @return True if loaded, otherwise false
   */
  getLoaded: () => boolean;

  /**
   * Returns all ObjectPool entities of type 'network'.
   * @return An array of ObjectPool entities of type 'network'
   */
  getNetworkObjects: () => ObjectPool[];

  /**
   * Returns all ObjectPool entities of type 'network_subnet'.
   * @return An array of ObjectPool entities of type 'network_subnet'
   */
  getNetworkSubnetObjects: () => ObjectPool[];

  /**
   * Returns all ObjectPool entities of type 'website'.
   * @return An array of ObjectPool entities of type 'website'
   */
  getWebsiteObjects: () => ObjectPool[];

  /**
   * Returns all ObjectPool entities of type 'keyword'.
   * @return An array of ObjectPool entities of type 'keyword'
   */
  getKeywordObjects: () => ObjectPool[];

  /**
   * Returns all ObjectPool entities of type 'mac'.
   * @return An array of ObjectPool entities of type 'mac'
   */
  getMacObjects: () => ObjectPool[];

  /**
   * Returns all ObjectPool entities of type 'domain'.
   * @return An array of ObjectPool entities of type 'domain'
   */
  getDomainsObjects: () => ObjectPool[];

  /**
   * Returns a specific ObjectPool entity by its key.
   * @param key The key of the ObjectPool entity to retrieve
   * @return The ObjectPool entity if found, otherwise undefined
   */
  getObject: (key: string) => ObjectPool | undefined;

  /**
   * Resets the store to its initial state, clearing all ObjectPool entities and setting loaded to false.
   */
  reset: () => void;

  /**
   * Handles the result of fetching updated ObjectPool entities.
   * @param result The array of fetched ObjectPool entities
   */
  fetchUpdated: (result: ObjectPool[]) => void;

  /**
   * Asynchronously handles fetching ObjectPool entities using a GET request.
   */
  fetch: () => Promise<void>;

  /**
   * Asynchronously handles invalidating the current ObjectPool entities and fetching them again.
   */
  invalidateAndFetch: () => Promise<void>;

  /**
   * Asynchronously handles deleting an ObjectPool entity by its ID using a DELETE request.
   * @param objectId The ID of the ObjectPool entity to be deleted
   */
  deleteObject: (objectId: string) => Promise<void>;

  /**
   * Asynchronously handles adding a new ObjectPool entity using a PUT request.
   * @param entity The ObjectPool entity to be added
   * @return A promise that resolves to an object containing the added ObjectPool entity
   */
  addObject: (entity: ObjectPool) => Promise<{ result: ObjectPool }>;

  /**
   * Asynchronously handles saving an ObjectPool entity using a POST request.
   * @param entity The ObjectPool entity to be saved
   */
  saveObject: (entity: ObjectPool) => Promise<void>;

  /**
   * Retrieves YouTube categories. Asynchronously fetches from the API if not already loaded, preventing redundant calls.
   * @return A promise that resolves to an array of YouTubeCategory entities
   */
  getYoutubeCategories: () => Promise<YoutubeCategory[]>;
}

export const useObjectStore = create<State & Actions>((set, get) => ({
  objects: [],
  loaded: false,
  youtubeCategories: [],

  getObjects: () => {
    return get().objects;
  },

  getLoaded: () => {
    return get().loaded;
  },

  getNetworkObjects: () => {
    return get().objects.filter((object) => object.type === 0 || object.type === 1);
  },

  getNetworkSubnetObjects: () => {
    return get().objects.filter((object) => object.type === 1);
  },

  getWebsiteObjects: () => {
    return get().objects.filter((object) => object.type === 2);
  },

  getKeywordObjects: () => {
    return get().objects.filter((object) => object.type === 6);
  },

  getMacObjects: () => {
    return get().objects.filter((object) => object.type === 4);
  },

  getDomainsObjects: () => {
    return get().objects.filter((object) => object.type === 7);
  },

  getObject: (key: string) => {
    return get().objects.find((object) => object.id === key);
  },

  reset: () => {
    set({ objects: [], loaded: false });
  },

  fetchUpdated: (result: ObjectPool[]) => {
    set({ objects: result || [], loaded: true });
  },

  fetch: async () => {
    if (get().objects.length !== 0) return;

    const { data } = (await Api.getAsync("/config/device/ajax/aliases")) as { data: ObjectPool[] };
    get().fetchUpdated(data);
    set({ objects: data || [], loaded: true });
  },

  invalidateAndFetch: async () => {
    get().reset();
    await get().fetch();
  },

  deleteObject: async (objectId: string) => {
    await Api.deleteAsync("/config/device/ajax/aliases/" + objectId);
    await get().invalidateAndFetch();
  },

  addObject: async (entity: ObjectPool) => {
    const response = (await Api.putAsync("/config/device/ajax/aliases", entity)) as { result: ObjectPool };
    await get().invalidateAndFetch();
    return response;
  },

  saveObject: async (entity: ObjectPool) => {
    await Api.postAsync("/config/device/ajax/aliases", entity);
    await get().invalidateAndFetch();
  },

  getYoutubeCategories: async () => {
    if (get().youtubeCategories.length > 0) {
      return get().youtubeCategories;
    }

    const categories = await getYoutubeCategories();
    set({ youtubeCategories: categories });
    return categories;
  },
}));
